// =====================================================================================
// media-device-errors - Turns browser media failures into VideoErrorHandler codes
// =====================================================================================

import type { VideoError } from './types.js';

/**
 * Maps a `getUserMedia` failure onto one of the classifications
 * `VideoErrorHandler` already knows how to explain and recover from.
 *
 * The browser reports these as `DOMException`s distinguished only by `name`,
 * and LiveKit rethrows them unchanged, so the name is all there is to go on.
 */
export function classifyMediaDeviceError(
  err: unknown,
  device: 'microphone' | 'camera'
): VideoError {
  const name =
    typeof err === 'object' && err !== null && 'name' in err
      ? String((err as { name: unknown }).name)
      : '';
  const label = device === 'microphone' ? 'Microphone' : 'Camera';
  const timestamp = Date.now();

  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return {
        code: 'PERMISSION_DENIED',
        message: `${label} access was blocked. Allow it in your browser and try again.`,
        timestamp
      };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return {
        code: 'DEVICE_NOT_FOUND',
        message: `No ${device} was found. Connect one and try again.`,
        timestamp
      };
    case 'NotReadableError':
    case 'TrackStartError':
      return {
        code: 'DEVICE_NOT_FOUND',
        message: `Your ${device} could not be started — another application may be using it.`,
        timestamp
      };
    default:
      return {
        code: device === 'microphone' ? 'MIC_TOGGLE_FAILED' : 'CAMERA_TOGGLE_FAILED',
        message: `Failed to toggle ${device}. Please try again.`,
        timestamp
      };
  }
}
